import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';

export interface NotificationItem {
  notification_id: number;
  title: string;
  message: string;
  notification_type: string;
  reference_id: string | null;
  is_read: boolean;
  created_at: string;
}

interface NotificationListResponse {
  success: boolean;
  data: NotificationItem[];
  count: number;
}

interface MarkReadResponse {
  success: boolean;
  message: string;
  notification_id: number;
  is_read: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/notifications`;

  private readonly wsUrl = `${environment.wsUrl}/ws/notifications/`;

  private socket: WebSocket | null = null;

  private readonly notificationsSubject = new BehaviorSubject<NotificationItem[]>([]);

  private readonly unreadCountSubject = new BehaviorSubject<number>(0);

  private readonly toastr = inject(ToastrService);

  readonly notifications$ = this.notificationsSubject.asObservable();

  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  /**
   * Load notifications saved in the database.
   */
  // loadNotifications(): Observable<NotificationListResponse> {
  //   return this.http.get<NotificationListResponse>(`${this.apiUrl}/`).pipe(
  //     tap((response) => {
  //       this.setNotifications(response.data ?? []);
  //     })
  //   );
  // }

  /**
   * Connect to the separate notification WebSocket.
   * Pass the access token from the existing AuthenticationService.
   */
  connect(accessToken: string): void {
    if (!accessToken) {
      console.error('Cannot connect to notifications: access token is missing.');
      return;
    }

    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const url = `${this.wsUrl}?token=${encodeURIComponent(accessToken)}`;

    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      console.log('Notification WebSocket connected.');
    };

    this.socket.onmessage = (event: MessageEvent) => {
      try {
        const notification = JSON.parse(event.data) as NotificationItem;

        if (typeof notification.notification_id !== 'number' || !notification.title || typeof notification.is_read !== 'boolean') {
          console.warn('Unexpected notification message format.');
          return;
        }

        this.upsertNotification(notification);

        // Use the same method for live and pending notifications.
        this.showNotificationToast(notification);
      } catch (error) {
        console.error('Could not process notification message.', error);
      }
    };

    this.socket.onerror = (error) => {
      console.error('Notification WebSocket error.', error);
    };

    this.socket.onclose = (event) => {
      console.log('Notification WebSocket disconnected.', event.code);
    };
  }

  /**
   * Mark one notification as read.
   */
  markAsRead(notificationId: number): Observable<MarkReadResponse> {
    return this.http.patch<MarkReadResponse>(`${this.apiUrl}/${notificationId}/read/`, {}).pipe(
      tap((response) => {
        if (response.success && response.is_read) {
          const updated = this.notificationsSubject.value.map((item) =>
            item.notification_id === notificationId ? { ...item, is_read: true } : item
          );

          this.setNotifications(updated);
        }
      })
    );
  }

  dismissNotification(notificationId: number): Observable<{ success: boolean }> {
    return this.http.patch<{ success: boolean }>(`${this.apiUrl}/${notificationId}/dismiss/`, {}).pipe(
      tap((response) => {
        if (response.success) {
          const remaining = this.notificationsSubject.value.filter((item) => item.notification_id !== notificationId);

          this.setNotifications(remaining);
        }
      })
    );
  }

  /**
   * Close only the notification WebSocket.
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }
  }

  private readonly toastedNotificationIds = new Set<number>();

  private showNotificationToast(notification: NotificationItem): void {
    if (this.toastedNotificationIds.has(notification.notification_id)) {
      return;
    }

    this.toastedNotificationIds.add(notification.notification_id);

    this.toastr.success(notification.message, notification.title || 'Notification', {
      timeOut: 20000,
      closeButton: true,
      progressBar: true,
      positionClass: 'toast-bottom-right',
      tapToDismiss: true
    });
  }

  private setNotifications(notifications: NotificationItem[]): void {
    this.notificationsSubject.next(notifications);

    this.unreadCountSubject.next(notifications.filter((item) => !item.is_read).length);
  }

  private upsertNotification(notification: NotificationItem): void {
    const current = this.notificationsSubject.value;

    const updated = [notification, ...current.filter((item) => item.notification_id !== notification.notification_id)].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    this.setNotifications(updated);
  }

  loadNotifications(showPendingToasts = false): Observable<NotificationListResponse> {
    return this.http.get<NotificationListResponse>(`${this.apiUrl}/`).pipe(
      tap((response) => {
        const notifications = response.data ?? [];

        this.setNotifications(notifications);

        // Show saved unread notifications after login.
        if (showPendingToasts) {
          const pendingNotifications = notifications
            .filter((item) => !item.is_read)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, 5)
            .reverse();

          pendingNotifications.forEach((notification) => {
            this.showNotificationToast(notification);
          });
        }
      })
    );
  }
}
